import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Film, Sparkles, Loader2, Clock3, Monitor, Mic2 } from "lucide-react";
import { api, MODEL_LABELS } from "@/lib/api";

const DURATIONS = [15, 30, 60];
const RATIOS = [
  { v: "9:16", label: "Vertical 9:16" },
  { v: "1:1", label: "Carré 1:1" },
  { v: "16:9", label: "Paysage 16:9" },
];
const VOICES = ["Français Diaspora", "Afrique de l'Ouest", "Parisien Urbain", "Caribéen"];

export default function VideoGenerator({ seedPrompt, seedTrend, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("montage");
  const [duration, setDuration] = useState(60);
  const isStory = model === "story";
  const isMontage = model === "montage" || isStory;
  const [ratio, setRatio] = useState("9:16");
  const [voice, setVoice] = useState("Français Diaspora");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (seedPrompt) setPrompt(seedPrompt);
  }, [seedPrompt]);

  const submit = async (e) => {
    e.preventDefault();
    if (isMontage && !seedTrend) {
      toast.error(isStory ? "Choisissez d'abord un buzz : l'histoire est racontée à partir de sa vidéo et de ses commentaires."
        : "Choisissez d'abord une tendance : le montage utilise les captures du blogueur.");
      return;
    }
    if (!isStory && prompt.trim().length < 10) {
      toast.error("Prompt trop court (minimum 10 caractères).");
      return;
    }
    setSubmitting(true);
    const t = toast.loading(`Lancement de la génération ${MODEL_LABELS[model]}…`);
    try {
      const v = await api.generateVideo({
        prompt: prompt.trim() || seedTrend?.title || "Histoire du jour",
        model,
        duration_sec: duration,
        aspect_ratio: ratio,
        voice_style: voice,
        trend_id: seedTrend?.id,
        trend_title: seedTrend?.title,
      });
      toast.success(isStory ? "Histoire de 3-4 min lancée — prête dans ~5 min dans la galerie." : "Vidéo en cours de rendu — suis l'état dans la galerie.", { id: t });
      onCreated?.(v);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erreur de génération", { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="card-surface p-6 h-full flex flex-col"
      data-testid="video-generator-form"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-md bg-[#e7f3ff] border border-[#1877f240] flex items-center justify-center">
          <Film className="w-4 h-4 text-[color:var(--bcs-accent)]" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold leading-none">Studio IA · Génération vidéo</h2>
          <div className="text-[11px] text-[color:var(--bcs-text-dim)] font-mono-ui mt-1 tracking-wider">
            MONTAGE CAPTURES · VEO 3 · KLING · SEEDANCE
          </div>
        </div>
      </div>

      {seedTrend && (
        <div className="mb-4 rounded-lg border border-[#e4a11b55] bg-[#fff4d6] px-3 py-2">
          <div className="text-[10.5px] font-mono-ui tracking-widest uppercase text-[color:var(--bcs-accent-2)]">
            Prompt depuis tendance
          </div>
          <div className="text-sm mt-0.5">{seedTrend.title}</div>
        </div>
      )}

      <label className="block mb-1 text-xs font-mono-ui tracking-wider uppercase text-[color:var(--bcs-text-dim)]">
        {isStory ? "Angle de l'histoire (facultatif)" : isMontage ? "Texte de la voix off" : "Prompt vidéo"}
      </label>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={5}
        placeholder={isStory ? "Ex : insistez sur les leçons à retenir pour les entrepreneurs, ou racontez-le comme une enquête…" : "Ex: Split-screen vertical 9:16 d'une influenceuse diaspora Paris à gauche et un rappeur francophone à droite, flashs néon rose, overlay 'BREAKING TEA', lower-thirds style tabloïd, 15 secondes, énergie Shade Room…"}
        className="w-full rounded-lg bg-[#f7f8fa] border border-[#dddfe2] px-3.5 py-3 text-sm placeholder:text-[color:var(--bcs-text-dim)] focus:border-[color:var(--bcs-accent)] focus:outline-none transition-colors resize-none"
        data-testid="video-prompt-input"
      />

      <div className="mt-4">
        <div className="text-xs font-mono-ui tracking-wider uppercase text-[color:var(--bcs-text-dim)] mb-2">
          Modèle
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.keys(MODEL_LABELS).filter((m) => m !== "digest").map((m) => (
            <button
              type="button"
              key={m}
              onClick={() => setModel(m)}
              className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${
                model === m
                  ? "border-[color:var(--bcs-accent)] bg-[#1877f210]"
                  : "border-[#dddfe2] hover:border-[#bcc0c4]"
              }`}
              data-testid={`model-${m}`}
            >
              <div className="font-display font-semibold text-sm">{MODEL_LABELS[m].split(" · ")[0]}</div>
              <div className="font-mono-ui text-[10px] text-[color:var(--bcs-text-dim)] uppercase mt-0.5">
                {MODEL_LABELS[m].split(" · ")[1] || "AI"}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={`mt-4 grid gap-3 ${isStory ? "grid-cols-2" : "grid-cols-3"}`}>
        <div>
          <div className="text-xs font-mono-ui tracking-wider uppercase text-[color:var(--bcs-text-dim)] mb-2 flex items-center gap-1">
            <Clock3 className="w-3 h-3" /> Durée
          </div>
          {isStory ? (
            <div className="rounded-md border border-[color:var(--bcs-accent)] text-[#1877f2] bg-[#e7f3ff] px-2 py-1.5 text-xs font-mono-ui font-semibold text-center" data-testid="duration-story">3-4 min</div>
          ) : (
          <div className="flex gap-1">
            {DURATIONS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setDuration(d)}
                className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-mono-ui font-semibold transition-colors ${
                  duration === d
                    ? "border-[color:var(--bcs-accent)] text-[#1877f2] bg-[#e7f3ff]"
                    : "border-[#dddfe2] text-[color:var(--bcs-text-mut)]"
                }`}
                data-testid={`duration-${d}`}
              >
                {d}s
              </button>
            ))}
          </div>
          )}
        </div>

        {!isStory && <div>
          <div className="text-xs font-mono-ui tracking-wider uppercase text-[color:var(--bcs-text-dim)] mb-2 flex items-center gap-1">
            <Monitor className="w-3 h-3" /> Ratio
          </div>
          <Select value={ratio} onValueChange={setRatio}>
            <SelectTrigger className="h-8 text-xs bg-[#f7f8fa] border-[#dddfe2]" data-testid="ratio-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RATIOS.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>}

        <div>
          <div className="text-xs font-mono-ui tracking-wider uppercase text-[color:var(--bcs-text-dim)] mb-2 flex items-center gap-1">
            <Mic2 className="w-3 h-3" /> Voix
          </div>
          <Select value={voice} onValueChange={setVoice}>
            <SelectTrigger className="h-8 text-xs bg-[#f7f8fa] border-[#dddfe2]" data-testid="voice-select"><SelectValue /></SelectTrigger>
            <SelectContent>{VOICES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting || (!isStory && !prompt.trim())}
        className="btn-primary mt-5 w-full justify-center"
        data-testid="generate-video-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {submitting ? "Lancement…" : isStory ? "Générer l'histoire (3-4 min)" : `Générer la vidéo (${duration}s)`}
      </button>

      <p className="mt-3 text-[10.5px] font-mono-ui text-[color:var(--bcs-text-dim)] tracking-wide leading-relaxed">
        MONTAGE : VRAIES CAPTURES + VOIX OFF (60 S), PRÊT EN ~1 MIN · HISTOIRE : INTRO, PASSAGES CLÉS DE LA VIDÉO SOURCE, RÉACTIONS, CONCLUSION — PRÊTE EN ~5 MIN · MODÈLES IA : 1-4 MIN.
      </p>
    </form>
  );
}
